/**
 * Admin gift order pricing section (PBI #1516).
 *
 * Customers see combined lines (Gift, Shipping, Sales tax). Finance needs the opposite:
 * every stored charge component next to Goody's latest quote and Goody's billed amounts,
 * so a mismatch between what we charged and what Goody debited is visible at a glance.
 */

import type { ReactNode } from 'react';
import { Divider, Group, Text } from '@mantine/core';
import type { AdminGiftOrderPricing } from '../../pages/admin/AdminGiftOrdersPage';
import { formatInstantLocalShort } from '../../utils/formatDateTime';
import { formatUsdCents } from '../../utils/formatUsd';

interface PricingRow {
  label: string;
  cents: number | null | undefined;
}

interface AdminGiftOrderPricingSectionProps {
  pricing: AdminGiftOrderPricing;
}

const PricingRows = ({ rows }: { rows: PricingRow[] }) => (
  <>
    {rows.map((row) => (
      <Group key={row.label} justify="space-between" wrap="nowrap">
        <Text fw={500} size="sm">
          {row.label}
        </Text>
        <Text size="sm" ta="right">
          {formatUsdCents(row.cents)}
        </Text>
      </Group>
    ))}
  </>
);

const SectionHeading = ({ label, atUtc }: { label: string; atUtc?: string | null }): ReactNode => (
  <Divider label={atUtc ? `${label} · ${formatInstantLocalShort(atUtc)}` : label} />
);

/**
 * Renders what the customer saw, our stored components, Goody's quote, and Goody's billed amounts.
 * Goody sections only render once Goody numbers were saved for the order.
 */
export const AdminGiftOrderPricingSection = ({ pricing }: AdminGiftOrderPricingSectionProps) => (
  <>
    <SectionHeading label="Customer lines" />
    <PricingRows
      rows={[
        { label: 'Gift (all-in)', cents: pricing.customerGiftCents },
        { label: 'Shipping', cents: pricing.customerShippingCents },
        { label: 'Sales tax', cents: pricing.goodyTaxCents + pricing.platformFeeTaxCents },
      ]}
    />

    <SectionHeading label="Stored components" />
    <PricingRows
      rows={[
        { label: 'Product', cents: pricing.productCents },
        { label: 'Brand shipping', cents: pricing.brandShippingCents },
        { label: 'Goody fee on gift', cents: pricing.goodyFeeOnProductCents },
        { label: 'Goody fee on shipping', cents: pricing.goodyFeeOnShippingCents },
        { label: 'Platform fee', cents: pricing.platformFeeCents },
        { label: 'Goody sales tax', cents: pricing.goodyTaxCents },
        { label: 'Platform fee tax', cents: pricing.platformFeeTaxCents },
      ]}
    />

    {pricing.goodyQuotedAtUtc && (
      <>
        <SectionHeading label="Goody quote (latest)" atUtc={pricing.goodyQuotedAtUtc} />
        <PricingRows
          rows={[
            { label: 'Quoted product', cents: pricing.goodyQuotedProductCents },
            { label: 'Quoted shipping', cents: pricing.goodyQuotedShippingCents },
            { label: 'Quoted processing fee', cents: pricing.goodyQuotedProcessingFeeCents },
            { label: 'Quoted pre-tax', cents: pricing.goodyQuotedPreTaxCents },
            { label: 'Quoted est. tax (low)', cents: pricing.goodyQuotedEstTaxLowCents },
            { label: 'Quoted est. tax (high)', cents: pricing.goodyQuotedEstTaxHighCents },
            { label: 'Quoted tax', cents: pricing.goodyQuotedTaxCents },
            { label: 'Quoted total', cents: pricing.goodyQuotedTotalCents },
          ]}
        />
      </>
    )}

    {pricing.goodyAmountsUpdatedAtUtc && (
      <>
        <SectionHeading label="Goody billed" atUtc={pricing.goodyAmountsUpdatedAtUtc} />
        <PricingRows
          rows={[
            { label: 'Billed product', cents: pricing.goodyAmountProductCents },
            { label: 'Billed shipping', cents: pricing.goodyAmountShippingCents },
            { label: 'Billed processing fee', cents: pricing.goodyAmountProcessingFeeCents },
            { label: 'Billed pre-tax', cents: pricing.goodyAmountPreTaxCents },
            { label: 'Billed tax', cents: pricing.goodyAmountTaxCents },
            { label: 'Billed total', cents: pricing.goodyAmountTotalCents },
            { label: 'Global Relay cost', cents: pricing.goodyAmountGlobalRelayCostCents },
          ]}
        />
      </>
    )}
  </>
);
